/**
 * ActiviteEditionController — page dédiée d'édition (ou création) d'une
 * activité §2.I (EPIC-054 / 54.6 — migration depuis l'ancienne modale
 * `<dialog id="modal-activity-form">`).
 *
 * Routes consommées :
 *  - `GET  /activities`                 (chargement de l'activité en édition)
 *  - `GET  /organization/profile`       (flags is_formation_provider / is_subject_to_rep)
 *  - `POST /activities`                 (création)
 *  - `PATCH /activities/{id}`           (édition, payload partiel)
 *
 * Les sous-blocs de l'onglet *Données complémentaires* sont conditionnels :
 *  - DPC visible si NAF commence par `86`.
 *  - NDA visible si `organization.activityContext.isFormationProvider`.
 *  - REP visible si `organization.activityContext.isSubjectToRep` ET
 *    `category === 'bic_vente'`.
 *
 * Convention "pas de modal", "pas de valeurs fictives en dur".
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const ERROR_MESSAGES = {
    invalid_naf_format: "Le code APE est invalide. Format attendu : NN.NNL (ex. 62.01Z).",
    validation_failed: "Certains champs sont invalides. Vérifiez votre saisie.",
    activity_archived: "Cette activité est archivée et ne peut plus être modifiée.",
    activity_not_found: "Cette activité n'existe pas ou a été supprimée.",
    invalid_request: "Requête invalide.",
    unauthorized: "Vous n'êtes pas autorisé à effectuer cette action.",
};

export default class ActiviteEditionController extends BaseController {
    static targets = [
        "crumb", "overline", "title",
        "tabs", "panel",
        "form", "submitBtn", "formError", "loadError",
        "immutableNotice",
        "categoryInput", "categoryHint",
        "startDateInput", "startDateHint",
        "nafInput", "nafError",
        "descriptionInput", "locationInput",
        "regulatedInput", "artisanalInput",
        "assurancesEmpty",
        "rcProBlock", "rcProInsurerInput", "rcProNumberInput", "rcProExpInput",
        "decennaleBlock", "decennaleInsurerInput", "decennaleNumberInput", "decennaleExpInput",
        "federationNameInput", "federationRenewalInput",
        "dpcBlock", "dpcInput",
        "ndaBlock", "ndaInput",
        "repBlock", "repEcoInput", "repRenewalInput",
        "metadataHint",
    ];

    connect() {
        const params = new URLSearchParams(window.location.search);
        this.editingId = params.get("id") ?? null;
        this._activityCache = null;
        this._orgContext = { isFormationProvider: false, isSubjectToRep: false };

        if (this.editingId) {
            this.titleTarget.textContent = "Modifier l'activité";
            this.crumbTarget.textContent = "Modifier l'activité";
            document.title = "Notamicro — Modifier l'activité";
        }

        this.loadContext();
    }

    async loadContext() {
        try {
            const orgProfile = await api.get("/organization/profile");
            const ctx = orgProfile?.activityContext;
            if (ctx && typeof ctx === "object") {
                this._orgContext = {
                    isFormationProvider: !!ctx.isFormationProvider,
                    isSubjectToRep: !!ctx.isSubjectToRep,
                };
            }
        } catch {
            // Best-effort : si on ne récupère pas les flags, on cache les
            // sections optionnelles (DPC reste piloté par NAF).
        }

        if (this.editingId) {
            await this.loadActivity();
        } else {
            this.immutableNoticeTarget.hidden = false;
            this.refreshConditionalSections();
        }
    }

    async loadActivity() {
        try {
            const response = await api.get("/activities");
            const items = Array.isArray(response?.activities) ? response.activities : [];
            const activity = items.find((a) => a.id === this.editingId) ?? null;
            if (!activity) {
                this.showLoadError("Activité introuvable.");
                return;
            }
            this._activityCache = activity;
            this.fillForm(activity);
            this.refreshConditionalSections();
        } catch {
            this.showLoadError("Impossible de charger cette activité. Réessayez plus tard.");
        }
    }

    fillForm(activity) {
        this.categoryInputTarget.value = activity.category ?? "";
        this.categoryInputTarget.disabled = true;
        this.categoryHintTarget.textContent =
            "Non modifiable après création (un changement de catégorie nécessite un parcours dédié).";

        const sd = activity.startDate ?? "";
        this.startDateInputTarget.value = sd.length >= 10 ? sd.slice(0, 10) : sd;
        this.startDateInputTarget.disabled = true;
        this.startDateHintTarget.textContent =
            "Non modifiable (date fixée par la formalité INPI initiale).";

        this.nafInputTarget.value = activity.nafCode ?? "";
        this.descriptionInputTarget.value = activity.description ?? "";
        this.locationInputTarget.value = activity.location ?? "";
        this.regulatedInputTarget.value = activity.regulatedCategory ?? "";
        this.artisanalInputTarget.checked = !!activity.isArtisanal;

        this.rcProInsurerInputTarget.value = activity.rcProInsurer ?? "";
        this.rcProNumberInputTarget.value = activity.rcProNumber ?? "";
        const rcExp = activity.rcProExpirationDate ?? "";
        this.rcProExpInputTarget.value = rcExp.length >= 10 ? rcExp.slice(0, 10) : rcExp;

        this.decennaleInsurerInputTarget.value = activity.decennaleInsurer ?? "";
        this.decennaleNumberInputTarget.value = activity.decennaleNumber ?? "";
        const decExp = activity.decennaleExpirationDate ?? "";
        this.decennaleExpInputTarget.value = decExp.length >= 10 ? decExp.slice(0, 10) : decExp;

        const meta = activity.metadata && typeof activity.metadata === "object"
            ? activity.metadata
            : {};
        this.federationNameInputTarget.value = meta.professional_federation_name ?? "";
        this.federationRenewalInputTarget.value = meta.professional_federation_renewal_date ?? "";
        this.dpcInputTarget.value = meta.dpc_last_completion_date ?? "";
        this.ndaInputTarget.value = meta.nda_number ?? "";
        this.repEcoInputTarget.value = meta.rep_eco_organism ?? "";
        this.repRenewalInputTarget.value = meta.rep_membership_renewal_date ?? "";

        const role = activity.role ?? "";
        const overline = role === "principal" ? "Activité principale" : "Activité secondaire";
        this.overlineTarget.textContent = "Profil entreprise · " + overline;
    }

    switchTab(event) {
        const target = event.currentTarget;
        const name = target.dataset.tab;
        if (!name) return;
        this.activateTab(name);
        target.focus();
    }

    handleKey(event) {
        if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
        const tabs = Array.from(this.tabsTarget.querySelectorAll("[role='tab']"));
        const idx = tabs.indexOf(event.currentTarget);
        if (idx === -1) return;
        const delta = event.key === "ArrowRight" ? 1 : -1;
        const next = tabs[(idx + delta + tabs.length) % tabs.length];
        if (next) {
            this.activateTab(next.dataset.tab);
            next.focus();
        }
    }

    activateTab(name) {
        const tabs = this.tabsTarget.querySelectorAll("[role='tab']");
        tabs.forEach((t) => {
            const active = t.dataset.tab === name;
            t.classList.toggle("is-active", active);
            t.setAttribute("aria-selected", active ? "true" : "false");
        });
        this.panelTargets.forEach((p) => {
            p.hidden = p.dataset.panel !== name;
        });
    }

    regulatedChange() {
        this.refreshConditionalSections();
    }

    refreshConditionalSections() {
        const reg = this.regulatedInputTarget.value;
        this.rcProBlockTarget.hidden = reg === "";
        this.decennaleBlockTarget.hidden = reg !== "batiment";
        this.assurancesEmptyTarget.hidden = !(this.rcProBlockTarget.hidden && this.decennaleBlockTarget.hidden);

        const naf = (this.nafInputTarget.value ?? "").toUpperCase();
        const category = this.categoryInputTarget.value;
        this.dpcBlockTarget.hidden = !naf.startsWith("86");
        this.ndaBlockTarget.hidden = !this._orgContext.isFormationProvider;
        this.repBlockTarget.hidden = !(this._orgContext.isSubjectToRep && category === "bic_vente");
    }

    async submit(event) {
        if (event) event.preventDefault();
        if (!this.formTarget.checkValidity()) {
            this.formTarget.reportValidity();
            return;
        }

        this.clearErrors();
        this.submitBtnTarget.disabled = true;

        const payload = this.buildPayload();
        try {
            if (this.editingId) {
                await api.updateActivity(this.editingId, payload);
                this.showSuccess("Activité modifiée.");
            } else {
                await api.createActivity(payload);
                this.showSuccess("Activité ajoutée. Une formalité INPI a été préparée.");
            }
            window.location.assign("/profil-entreprise.html#activites");
        } catch (err) {
            this.renderError(err);
            this.submitBtnTarget.disabled = false;
        }
    }

    buildPayload() {
        const fd = new FormData(this.formTarget);
        const get = (name) => {
            const v = fd.get(name);
            return v === null ? null : (typeof v === "string" ? v.trim() : v);
        };
        const orNull = (v) => (v === "" || v === null || v === undefined ? null : v);

        const payload = {
            nafCode: (get("nafCode") || "").toUpperCase(),
            description: get("description") || "",
            location: orNull(get("location")),
            regulatedCategory: orNull(get("regulatedCategory")),
            isArtisanal: fd.get("isArtisanal") === "on",
            rcProNumber: orNull(get("rcProNumber")),
            rcProInsurer: orNull(get("rcProInsurer")),
            rcProExpirationDate: orNull(get("rcProExpirationDate")),
            decennaleNumber: orNull(get("decennaleNumber")),
            decennaleInsurer: orNull(get("decennaleInsurer")),
            decennaleExpirationDate: orNull(get("decennaleExpirationDate")),
        };

        if (!this.editingId) {
            payload.category = get("category") || "";
            payload.startDate = get("startDate") || "";
        }

        const metadata = {};
        const metaFields = {
            professional_federation_name: get("professional_federation_name"),
            professional_federation_renewal_date: get("professional_federation_renewal_date"),
            dpc_last_completion_date: get("dpc_last_completion_date"),
            nda_number: get("nda_number"),
            rep_eco_organism: get("rep_eco_organism"),
            rep_membership_renewal_date: get("rep_membership_renewal_date"),
        };
        for (const [k, v] of Object.entries(metaFields)) {
            metadata[k] = (v === "" || v === null) ? null : v;
        }
        payload.metadata = metadata;

        return payload;
    }

    clearErrors() {
        this.nafErrorTarget.textContent = "";
        this.nafErrorTarget.hidden = true;
        this.formErrorTarget.textContent = "";
        this.formErrorTarget.hidden = true;
        this.nafInputTarget.setAttribute("aria-invalid", "false");
    }

    renderError(err) {
        const code = err?.data?.error?.code ?? err?.error?.code ?? null;
        const serverMsg = err?.data?.error?.message ?? err?.message ?? null;
        const friendly = (code && ERROR_MESSAGES[code]) || serverMsg ||
            "Erreur lors de l'enregistrement.";

        if (code === "invalid_naf_format") {
            this.nafErrorTarget.textContent = friendly;
            this.nafErrorTarget.hidden = false;
            this.nafInputTarget.setAttribute("aria-invalid", "true");
            this.nafInputTarget.focus();
            this.activateTab("identite");
            return;
        }

        this.formErrorTarget.textContent = friendly;
        this.formErrorTarget.hidden = false;
    }

    showLoadError(message) {
        this.loadErrorTarget.textContent = message;
        this.loadErrorTarget.hidden = false;
    }
}
